import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, UserPlus, Heart, MessageCircle, Users, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotificationsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const dropdownRef = useRef(null);

  // Fetch notifications and friend requests
  useEffect(() => {
    fetchData();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      
      // Fetch notifications
      const [notifResponse, requestsResponse, countResponse] = await Promise.all([
        axios.get(`${API}/notifications?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/friends/requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setNotifications(notifResponse.data);
      setFriendRequests(requestsResponse.data);
      setUnreadCount(countResponse.data.count + requestsResponse.data.length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleAcceptRequest = async (requestId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/friends/accept/${requestId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devotee request accepted');
      fetchData();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/friends/reject/${requestId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'friend_request':
        return <UserPlus className="w-4 h-4 text-[#FF9933]" />;
      case 'friend_accepted':
        return <Users className="w-4 h-4 text-green-500" />;
      case 'like':
        return <Heart className="w-4 h-4 text-red-500" />;
      case 'comment':
      case 'story_reply':
        return <MessageCircle className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors relative"
        data-testid="notifications-btn"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[320px] sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-[9999]" style={{ maxWidth: 'calc(100vw - 24px)' }}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-[#FF9933] hover:text-[#E68A00]"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'all' 
                  ? 'text-[#FF9933] border-b-2 border-[#FF9933]' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === 'requests' 
                  ? 'text-[#FF9933] border-b-2 border-[#FF9933]' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Devotee Requests
              {friendRequests.length > 0 && (
                <span className="absolute top-1 right-1/4 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </button>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto overflow-x-hidden">
            {activeTab === 'requests' ? (
              // Friend Requests Tab
              friendRequests.length === 0 ? (
                <div className="p-6 text-center">
                  <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No friend requests</p>
                </div>
              ) : (
                friendRequests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-start gap-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <Link to={`/profile/${request.sender_id}`}>
                      <img
                        src={request.sender_avatar || 'https://images.unsplash.com/photo-1761383396518-e2e30968a25f?w=100&h=100&fit=crop'}
                        alt={request.sender_name}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                    </Link>
                    
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/profile/${request.sender_id}`}
                        className="font-medium text-gray-900 hover:text-[#FF9933] text-sm"
                      >
                        {request.sender_name}
                      </Link>
                      <p className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                      </p>
                      
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={(e) => handleAcceptRequest(request.id, e)}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-[#FF9933] hover:bg-[#E68A00] text-white text-xs font-medium rounded-lg transition-colors"
                        >
                          <Check className="w-3 h-3" />
                          Accept
                        </button>
                        <button
                          onClick={(e) => handleRejectRequest(request.id, e)}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors"
                        >
                          <X className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )
            ) : (
              // All Notifications Tab
              notifications.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <Link
                    key={notif.id}
                    to={notif.type === 'friend_accepted' ? `/profile/${notif.from_user_id}` : '#'}
                    className={`flex items-start gap-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                      !notif.is_read ? 'bg-orange-50/50' : ''
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <img
                        src={notif.from_user_avatar || 'https://images.unsplash.com/photo-1761383396518-e2e30968a25f?w=100&h=100&fit=crop'}
                        alt={notif.from_user_name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                        {getNotificationIcon(notif.type)}
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        {notif.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    
                    {!notif.is_read && (
                      <div className="w-2 h-2 bg-[#FF9933] rounded-full flex-shrink-0 mt-2" />
                    )}
                  </Link>
                ))
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
